/**
 * Type-safe API helpers for the M10 Vendor Review backend.
 *
 * All requests target `/api/v1/quality/*` (proxied to the NestJS backend in
 * dev via Vite, and same-origin in production).
 */
import { apiRequest } from './client.js';

export interface ReviewRow {
  readonly reviewId: string;
  readonly status: string;
  readonly completionId?: string;
  readonly vendorId?: string;
  readonly painterUserId?: string;
  readonly assignedReviewerUserId?: string | null;
  readonly createdAt?: string;
  readonly updatedAt?: string;
}

export interface DashboardResponse {
  readonly rows: readonly ReviewRow[];
  readonly total: number;
}

export interface DashboardQuery {
  readonly status?: string;
  readonly vendorId?: string;
  readonly painterUserId?: string;
  readonly reviewerUserId?: string;
  readonly dateFrom?: string;
  readonly dateTo?: string;
  readonly limit?: number;
  readonly offset?: number;
}

function toQuery(q?: DashboardQuery): string {
  if (!q) return '';
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) {
    if (v !== undefined && v !== null && v !== '') sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? '?' + s : '';
}

export async function listReviews(q?: DashboardQuery, token?: string | null): Promise<DashboardResponse> {
  return apiRequest<DashboardResponse>('/v1/quality/reviews' + toQuery(q), { token });
}

export async function getReview(id: string, token?: string | null): Promise<ReviewRow> {
  return apiRequest<ReviewRow>(`/v1/quality/reviews/${id}`, { token });
}

export async function startReview(id: string, token?: string | null): Promise<ReviewRow> {
  return apiRequest<ReviewRow>(`/v1/quality/reviews/${id}/start`, { method: 'POST', token });
}

export async function approveReview(id: string, remarks: string, token?: string | null): Promise<ReviewRow> {
  return apiRequest<ReviewRow>(`/v1/quality/reviews/${id}/approve`, {
    method: 'POST',
    body: { remarks },
    token,
  });
}

export async function rejectReview(
  id: string,
  reasonCode: string,
  remarks: string,
  token?: string | null,
): Promise<ReviewRow> {
  return apiRequest<ReviewRow>(`/v1/quality/reviews/${id}/reject`, {
    method: 'POST',
    body: { reasonCode, remarks },
    token,
  });
}

export async function forwardToBO(id: string, reason: string, token?: string | null): Promise<ReviewRow> {
  return apiRequest<ReviewRow>(`/v1/quality/reviews/${id}/forward-bo`, {
    method: 'POST',
    body: { reason },
    token,
  });
}

export async function escalate(id: string, reason: string, token?: string | null): Promise<ReviewRow> {
  return apiRequest<ReviewRow>(`/v1/quality/reviews/${id}/escalate`, {
    method: 'POST',
    body: { reason },
    token,
  });
}

export interface PhotoChecklist {
  readonly [key: string]: boolean;
}

export type PhotoDecision = 'APPROVED' | 'REJECTED' | 'NEEDS_REVIEW';

export async function decidePhoto(
  reviewId: string,
  photoId: string,
  checklist: PhotoChecklist,
  decision: PhotoDecision,
  rejectReasonCode?: string,
  rejectRemarks?: string,
  token?: string | null,
): Promise<{ validationId: string }> {
  return apiRequest(`/v1/quality/reviews/${reviewId}/photos/${photoId}`, {
    method: 'POST',
    body: { checklist, decision, rejectReasonCode, rejectRemarks },
    token,
  });
}

export async function listPhotos(reviewId: string, token?: string | null): Promise<readonly unknown[]> {
  return apiRequest(`/v1/quality/reviews/${reviewId}/photos`, { token });
}

export async function validateGps(
  reviewId: string,
  body: {
    gpsId: string;
    siteLatitude: number;
    siteLongitude: number;
    execLatitude: number;
    execLongitude: number;
    accuracyM: number;
  },
  token?: string | null,
): Promise<{ gpsValidationId: string }> {
  return apiRequest(`/v1/quality/reviews/${reviewId}/gps`, { method: 'POST', body, token });
}

export async function listGps(reviewId: string, token?: string | null): Promise<readonly unknown[]> {
  return apiRequest(`/v1/quality/reviews/${reviewId}/gps`, { token });
}

export async function validateArea(
  reviewId: string,
  body: { executionAreaId: string; approvedAreaSqm: number; executedAreaSqm: number },
  token?: string | null,
): Promise<{ areaValidationId: string }> {
  return apiRequest(`/v1/quality/reviews/${reviewId}/areas`, { method: 'POST', body, token });
}

export async function listAreas(reviewId: string, token?: string | null): Promise<readonly unknown[]> {
  return apiRequest(`/v1/quality/reviews/${reviewId}/areas`, { token });
}

export async function validateMaterial(
  reviewId: string,
  body: {
    executionMaterialId: string;
    materialCode: string;
    approvedQty: number;
    consumedQty: number;
  },
  token?: string | null,
): Promise<{ materialValidationId: string }> {
  return apiRequest(`/v1/quality/reviews/${reviewId}/materials`, { method: 'POST', body, token });
}

export async function listMaterials(reviewId: string, token?: string | null): Promise<readonly unknown[]> {
  return apiRequest(`/v1/quality/reviews/${reviewId}/materials`, { token });
}

export async function requestRework(
  reviewId: string,
  correctionInstructions: string,
  token?: string | null,
): Promise<{ reworkRequestId: string }> {
  return apiRequest(`/v1/quality/reviews/${reviewId}/rework`, {
    method: 'POST',
    body: { correctionInstructions },
    token,
  });
}

export async function listRework(reviewId: string, token?: string | null): Promise<readonly unknown[]> {
  return apiRequest(`/v1/quality/reviews/${reviewId}/rework`, { token });
}

export async function openEvidence(
  reviewId: string,
  body: { requestedKinds: readonly string[]; instructions: string },
  token?: string | null,
): Promise<{ evidenceRequestId: string }> {
  return apiRequest(`/v1/quality/reviews/${reviewId}/evidence`, { method: 'POST', body, token });
}

export async function listEvidence(reviewId: string, token?: string | null): Promise<readonly unknown[]> {
  return apiRequest(`/v1/quality/reviews/${reviewId}/evidence`, { token });
}

export async function recordScore(
  reviewId: string,
  body: { qualityRating: number; photoRating: number; gpsRating: number; areaRating: number },
  token?: string | null,
): Promise<{ scoreId: string }> {
  return apiRequest(`/v1/quality/reviews/${reviewId}/score`, { method: 'POST', body, token });
}

export async function getScore(reviewId: string, token?: string | null): Promise<unknown> {
  return apiRequest(`/v1/quality/reviews/${reviewId}/score`, { token });
}

export async function recordPerformance(
  reviewId: string,
  body: {
    qualityScoreDelta?: number;
    reworkCountDelta?: number;
    completionRateDelta?: number;
    reviewScoreDelta?: number;
    delayPctDelta?: number;
    compliancePctDelta?: number;
  },
  token?: string | null,
): Promise<{ perfUpdateId: string }> {
  return apiRequest(`/v1/quality/reviews/${reviewId}/performance`, { method: 'POST', body, token });
}

export async function listAudit(reviewId: string, token?: string | null): Promise<readonly unknown[]> {
  return apiRequest(`/v1/quality/reviews/${reviewId}/audit`, { token });
}

export type RemarkKind =
  | 'REVIEW_OPEN'
  | 'PHOTO_REJECT'
  | 'GPS_OVERRIDE'
  | 'AREA_OVERRIDE'
  | 'MATERIAL_OVERRIDE'
  | 'REWORK_INSTRUCT'
  | 'EVIDENCE_REQUEST'
  | 'APPROVAL'
  | 'REJECTION'
  | 'ESCALATION';

export async function postRemark(
  reviewId: string,
  body: { remarkKind: RemarkKind; remarkText: string },
  token?: string | null,
): Promise<{ remarkId: string }> {
  return apiRequest(`/v1/quality/reviews/${reviewId}/remarks`, { method: 'POST', body, token });
}

export async function listRemarks(reviewId: string, token?: string | null): Promise<readonly unknown[]> {
  return apiRequest(`/v1/quality/reviews/${reviewId}/remarks`, { token });
}
